// bk07 Lavanda Tushi — bridal-shower catalog card (mauve + cream).
//
// Paper-invitation feel: an outer mauve double-hairline mat wraps an
// ivory inner card; the focal piece is a soft lavender-sprig medallion;
// names sit in Cormorant Garamond; a small-caps caption closes the card.
// All colors flow from the LayoutCtx so palette swaps are trivial.
import { Photo, type LayoutCtx } from "./_shared";

export function BookieMauvePreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  // Slightly cooled paper mat so the inner card pops against it.
  const mat = `color-mix(in srgb, ${bg} 80%, ${accent} 18%)`;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: mat,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          position: "relative",
          // Inner hairline + a narrow band that reads as a printed double rule.
          boxShadow: `inset 0 0 0 1px ${accent}55, inset 0 0 0 6px ${bg}, inset 0 0 0 7px ${accent}33`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TAKLIFNOMA
        </div>

        {/* Lavender-sprig medallion. */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
            borderRadius: "50%",
            background: `radial-gradient(circle at 50% 45%, ${accent}1F, ${bg} 70%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `inset 0 0 0 1px ${accent}55`,
            overflow: "hidden",
          }}
        >
          <Photo
            src={photo}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", borderRadius: "50%" }}
            fallback={
              <svg viewBox="0 0 100 100" style={{ width: "78%", height: "78%" }} aria-hidden>
                <line x1="50" y1="22" x2="50" y2="80" stroke={accent} strokeWidth="1.1" />
                {[28, 36, 44, 52, 60, 68].map((y, i) => (
                  <g key={y} opacity={0.85 - i * 0.05}>
                    <ellipse cx="50" cy={y} rx="3.4" ry="2" fill={accent} />
                    <ellipse cx={46 - (i % 2)} cy={y + 1} rx="2.2" ry="1.4" fill={accent} opacity="0.6" />
                    <ellipse cx={54 + (i % 2)} cy={y + 1} rx="2.2" ry="1.4" fill={accent} opacity="0.6" />
                  </g>
                ))}
                <path d="M50 78 Q42 84 38 80" stroke={accent} strokeWidth="1" fill="none" />
                <path d="M50 78 Q58 84 62 80" stroke={accent} strokeWidth="1" fill="none" />
              </svg>
            }
          />
        </div>

        <div
          style={{
            fontFamily: font,
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 500,
            color: ink,
            letterSpacing: "0.01em",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TAKLIFNOMA · {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
